import React, { useState } from 'react';
import { toast } from '../components/ui/sonner';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrivateAccess = () => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    investmentCapacity: '',
    interests: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Get current language
      const currentLanguage = localStorage.getItem('maisonEzeLanguage') || 'en';
      
      const response = await axios.post(`${BACKEND_URL}/api/inquiries`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        investmentCapacity: formData.investmentCapacity,
        interests: formData.interests,
        message: formData.message,
        language: currentLanguage
      });
      
      if (response.data.success) {
        // Show success notification
        toast.success(`${t.contact.inquiryNumber} #${response.data.inquiry_number} submitted successfully!`, {
          description: t.contact.thankYouMessage,
          duration: 6000,
          style: {
            background: '#f0fdf4',
            border: '1px solid #86efac',
            color: '#166534'
          }
        });
        
        // Clear form
        setFormData({
          name: '',
          email: '',
          phone: '',
          investmentCapacity: '',
          interests: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      
      // Show error notification
      toast.error('Failed to submit inquiry', {
        description: error.response?.data?.detail || 'Please try again or contact us directly at contact@maison-eze.com',
        duration: 6000,
        style: {
          background: '#fef2f2',
          border: '1px solid #fca5a5',
          color: '#991b1b'
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section-padding" style={{ background: '#f6f5e8' }}>
      <div className="container">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="heading-1 mb-6">{t.contact.title}</h2>
            <div className="w-20 h-px bg-gray-300 mx-auto mb-6"></div>
            <p className="body-regular" style={{ color: '#666666' }}>
              {t.contact.subtitle}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6" style={{ background: '#fffef2', padding: '60px', borderRadius: '0px' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="body-small block mb-2" style={{ color: '#333333', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t.contact.fullName} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 body-regular"
                  style={{ 
                    background: 'transparent', 
                    border: '1px solid rgba(51,51,51,0.2)', 
                    borderRadius: '0px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#333333'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(51,51,51,0.2)'}
                />
              </div>
              
              <div>
                <label className="body-small block mb-2" style={{ color: '#333333', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t.contact.email} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 body-regular"
                  style={{ 
                    background: 'transparent', 
                    border: '1px solid rgba(51,51,51,0.2)', 
                    borderRadius: '0px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#333333'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(51,51,51,0.2)'}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="body-small block mb-2" style={{ color: '#333333', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t.contact.phone} *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 body-regular"
                  style={{ 
                    background: 'transparent', 
                    border: '1px solid rgba(51,51,51,0.2)', 
                    borderRadius: '0px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#333333'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(51,51,51,0.2)'}
                />
              </div>
              
              <div>
                <label className="body-small block mb-2" style={{ color: '#333333', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t.contact.investmentCapacity} *
                </label>
                <select
                  name="investmentCapacity"
                  value={formData.investmentCapacity}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 body-regular"
                  style={{ 
                    background: '#fffef2', 
                    border: '1px solid rgba(51,51,51,0.2)', 
                    borderRadius: '0px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#333333'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(51,51,51,0.2)'}
                >
                  <option value="">{t.contact.selectRange}</option>
                  <option value="5-10M">€5M - €10M</option>
                  <option value="10-25M">€10M - €25M</option>
                  <option value="25-50M">€25M - €50M</option>
                  <option value="50M+">€50M+</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="body-small block mb-2" style={{ color: '#333333', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t.contact.preferredDestinations}
              </label>
              <input
                type="text"
                name="interests"
                value={formData.interests}
                onChange={handleChange}
                placeholder={t.contact.destinationsPlaceholder}
                className="w-full px-4 py-3 body-regular"
                style={{ 
                  background: 'transparent', 
                  border: '1px solid rgba(51,51,51,0.2)', 
                  borderRadius: '0px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#333333'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(51,51,51,0.2)'}
              />
            </div>
            
            <div>
              <label className="body-small block mb-2" style={{ color: '#333333', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t.contact.message}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="5"
                className="w-full px-4 py-3 body-regular"
                style={{ 
                  background: 'transparent', 
                  border: '1px solid rgba(51,51,51,0.2)', 
                  borderRadius: '0px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  resize: 'vertical'
                }}
                onFocus={(e) => e.target.style.borderColor = '#333333'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(51,51,51,0.2)'}
              />
            </div>
            
            <div className="border-t pt-6" style={{ borderColor: 'rgba(51,51,51,0.1)' }}>
              <p className="body-small mb-6" style={{ color: '#666666', lineHeight: 1.6 }}>
                {t.contact.nda}
              </p>
              
              <button 
                type="submit" 
                className="btn-primary w-full md:w-auto"
                disabled={submitting}
                style={{ opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
              >
                {submitting ? 'Submitting...' : t.contact.submit}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default PrivateAccess;
